import React from 'react'
import { FieldError, UseFormReturn } from 'react-hook-form'
import {
  CreateProductOrderDto,
  ProductCatalogDto,
  SupplierDto,
  SupplierType,
} from '@services/dtos'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Box, Button, FormHelperText, Grid, IconButton, TextField, Typography } from '@mui/material'
import { AddWrapperInput } from '@components/inputs/AddWrapperInput'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import SupplierForm from '../../SupplierForm'
import { getSuppliers, setSupplierFilters } from '@state/reducers/supplierReducer'
import { SearchableDataTable } from '@components/tables/SearchableDataTable'
import ProductCatalogForm from '../../productCatalog/ProductCatalogForm'
import { getProductCatalogs, setProductCatalogFilters } from '@state/reducers/productCatalogReducer'
import { useSelector } from '@state/store'
import { productOrderCatalogsColumns } from '@utils/gridColumns'
import { WrapperFormContent } from '../../WrapperFormContent'
import InputField from '@components/inputs/InputField'
import DeleteIcon from '@mui/icons-material/Delete'
import { resetSelectedProducts, setSelectedProducts } from '@state/reducers/productOrderReducer'

type Props = {
  useCreateForm: UseFormReturn<CreateProductOrderDto, object>
}

const ProductOrderStepCart: React.FC<Props> = ({ useCreateForm }) => {
  const { setValue, formState: { errors }, watch } = useCreateForm
  const dispatch = useAppDispatch()

  const { productCatalogs, productCatalogsLoading, selectedProducts, selectedSupplier } = useSelector(({ productCatalog, productOrder, supplier }) => ({
    productCatalogs: productCatalog.items,
    productCatalogsLoading: productCatalog.loading,
    selectedProducts: productOrder.selectedProducts,
    selectedSupplier: supplier.items.datas.find(s => s.id === watch('supplierId')) || null,
  }))

  const fetchSuppliers = async (search: string) => {
    dispatch(setSupplierFilters({ search, type: SupplierType.PRODUCT_SUPPLIER }))
    const page = await dispatch(getSuppliers()).unwrap()
    return page?.datas ?? []
  }

  const lineItems = watch('lineItems') ?? []
  const ids = lineItems.map(li => li.productId)

  const toggleRow = (row: ProductCatalogDto) => {
    const alreadySelected = ids.includes(row.id)

    const newLineItems = alreadySelected
      ? lineItems.filter(li => li.productId !== row.id)
      : [...lineItems, { productId: row.id, quantity: 1 }]

    setValue('lineItems', newLineItems, { shouldValidate: true })

    const newSelectedProducts = alreadySelected
      ? selectedProducts.filter(p => p.id !== row.id)
      : [...selectedProducts, row]

    dispatch(setSelectedProducts(newSelectedProducts))
  }

  const removeProduct = (productId: string) => {
    const newLineItems = lineItems.filter(li => li.productId !== productId)

    setValue('lineItems', newLineItems, { shouldValidate: true })

    const newSelectedProducts = selectedProducts.filter(p => p.id !== productId)

    dispatch(setSelectedProducts(newSelectedProducts))
  }

  const isRowSelected = (row: ProductCatalogDto) =>
    lineItems.some(li => li.productId === row.id)

  const updateQuantity = (productId: string, quantity: number | null) => {
    const newLineItems = lineItems.map(li =>
      li.productId === productId ? { ...li, quantity } : li
    )
    setValue('lineItems', newLineItems, { shouldValidate: true })
  }

  const supplierId = watch('supplierId')
  const extraCost = +(watch('extraCost') ?? 0)

  const estimatedExclVatPrice =
    lineItems.reduce((total, li) => {
      const product = selectedProducts.find(p => p.id === li.productId)
      if (!product) return total
      return total + product.priceExclVat * (li.quantity ?? 0)
    }, 0) + extraCost
  const estimatedTtcPrice = lineItems.reduce((total, li) => {
      const product = selectedProducts.find(p => p.id === li.productId)
      if (!product) return total
      return total + product.priceExclVat * (li.quantity ?? 0) * (1 + product.vatRate)
    }, 0) + extraCost

  const QuantityActionButton = ({ action, productId, quantity }: { action: 'ADD' | 'REMOVE', productId: string, quantity: number }) => (
    <span className="inline-flex items-center">
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          if (quantity <= 0 && action === 'REMOVE') return
          updateQuantity(productId, quantity + (action === 'ADD' ? 1 : -1))
        }}
        sx={{
          height: 55,
          paddingLeft: 1.5,
          paddingRight: 1.5,
          minWidth: 'auto',
          borderColor: '#e5e7eb',
        }}
      >
        {action === 'ADD' ? <i className="fas fa-plus" /> : <i className="fas fa-minus" />}
      </Button>
    </span>
  )

  return (
    <>
      <Grid
        item
        xs={12}
        md={8}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <AddWrapperInput
          legend="Fournisseur"
          addTooltipTitle="Ajouter un fournisseur"
          dialogContent={(close) => (
            <SupplierForm
              onClose={close}
              defaultValues={{ type: SupplierType.PRODUCT_SUPPLIER }}
            />
          )}
          onAfterClose={() => {
            dispatch(getSuppliers())
          }}
          input={
            <ServerAutocomplete<SupplierDto>
              value={selectedSupplier}
              fetchService={fetchSuppliers}
              onChange={(value: SupplierDto | null) => {
                useCreateForm.reset()
                dispatch(resetSelectedProducts())
                setValue('supplierId', value ? value.id : '')
                dispatch(setProductCatalogFilters({ search: undefined, page: 0, supplierId: value?.id, checkOrderExamAssociation: true }))
                dispatch(getProductCatalogs())
              }}
              getValue={(value) => value?.id?.toString() || null}
              getOptionLabel={(value) => {
                const supplier = value as SupplierDto
                return supplier ? supplier.name : ''
              }}
              debounceMs={300}
              placeholder="Rechercher un fournisseur"
              label="Fournisseur"
              helperText={errors.supplierId?.message}
              error={!!errors.supplierId}
              minSearchLength={0}
            />
          }
        />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mt: 2 }}>
          {supplierId ? (
            <SearchableDataTable
              label="Recherche produit"
              placeholder="Rechercher un produit du fournisseur"
              showConfirmDialog={(row) => row.canBeOrderedDueToFutureExam === false}
              confirmDialogText="Aucun examen futur n'est planifié en lien avec ce produit. Confirmer l'ajout au panier malgré tout ?"
              columns={productOrderCatalogsColumns}
              rows={productCatalogs.datas}
              totalCount={productCatalogs.totalCount}
              loading={productCatalogsLoading}
              onSearch={(search) => {
                dispatch(setProductCatalogFilters({ search, page: 0, supplierId, checkOrderExamAssociation: true }))
                dispatch(getProductCatalogs())
              }}
              onPageChange={(page) => {
                dispatch(setProductCatalogFilters({ page, supplierId, checkOrderExamAssociation: true }))
                dispatch(getProductCatalogs())
              }}
              AddFormComponent={({ onClose }) => (
                <ProductCatalogForm
                  defaultValues={{ supplierId }}
                  onClose={onClose}
                />
              )}
              enableRowActions
              isRowSelected={isRowSelected}
              onToggleRow={toggleRow}
            />
          ) : (
            <Box
              className="bg-gray-100 rounded-md"
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography color="textSecondary" variant="body2">
                Sélectionner le fournisseur afin de sélectionner ses produits
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <WrapperFormContent
          legend={`Panier ${selectedProducts.length > 0 ? `(${selectedProducts.length} produit${selectedProducts.length > 1 ? 's' : ''})` : ''}`}
          className="flex flex-col flex-1"
        >
          {errors.lineItems && (
            <FormHelperText error>
              {(errors.lineItems as unknown as FieldError).message}
            </FormHelperText>
          )}
          {selectedProducts.length > 0 ? (
            <Box sx={{ flex: 1, mb: 2 }} data-testid="selected-products">
              <div className="max-h-[50vh] overflow-y-auto space-y-3">
                {selectedProducts.map((product, index) => {
                  const li = lineItems.find(l => l.productId === product.id)
                  const quantity = li?.quantity
                  const errorMsg = errors.lineItems?.[index]?.quantity?.message
                  const hasError = !!errorMsg

                  return (
                    <Box key={product.id} display="flex" flexDirection="column">
                      <Typography variant="body2" fontWeight="bold">
                        {product.name}
                      </Typography>

                      <Box display="flex" justifyContent="space-between" alignItems="flex-end" width="100%">
                        <Box display="flex" alignItems="center" mt={1}>
                          <Box className="mr-2">
                            <IconButton
                              size="small"
                              onClick={() => removeProduct(product.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          <QuantityActionButton
                            action="REMOVE"
                            productId={product.id}
                            quantity={quantity ?? 0}
                          />
                          <TextField
                            label={'Quantité'}
                            onChange={(e) => {
                              const val = e.target.value
                              updateQuantity(product.id, val === '' ? null : parseInt(val))
                            }}
                            className="rounded-xl bg-gray-50 w-[100px]"
                            type='number'
                            value={quantity === null ? '' : quantity}
                            error={hasError}
                          />
                          <QuantityActionButton
                            action="ADD"
                            productId={product.id}
                            quantity={quantity ?? 0}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2">
                            {(product.priceExclVat * (quantity ?? 0)).toFixed(2)} € (HT)
                          </Typography>
                          <Typography variant="body2">
                            {(product.priceExclVat * (quantity ?? 0) * (1 + product.vatRate)).toFixed(2)} € (TTC)
                          </Typography>
                        </Box>
                      </Box>

                      
                      {hasError && <Box className='ml-[86px]'>
                        <FormHelperText error>
                          {errorMsg}
                        </FormHelperText>
                      </Box>}
                    </Box>
                  )
                })}
              </div>
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography color="textSecondary" variant="body2">
                Votre panier est vide
              </Typography>
            </Box>
          )}

          <InputField
            control={useCreateForm.control}
            name={'extraCost'}
            label={'Coût supplémentaire'}
            type={'number'}
          />

          <Box className="mt-4" data-testid="estimated-price">
            <Box display="flex" justifyContent="space-between" width="100%">
              <Typography color="textSecondary" variant="body2">
                Estimation
              </Typography>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {estimatedExclVatPrice.toFixed(2)} € (HT)
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {estimatedTtcPrice.toFixed(2)} € (TTC)
                </Typography>
              </Box>
            </Box>
          </Box>
        </WrapperFormContent>
      </Grid>
    </>
  )
}

export default ProductOrderStepCart

